/* eslint-disable jira/filename-case */

import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { startGlobalIssueCreateTTIWithAdjustments } from '@jira/issue-adjustments__analytics/src/stopTTI.tsx';
import { CreateIssueUFOExperience } from '@jira/issue-create__issue-create-analytics/src/services/experiences/index.tsx';
import {
	globalIssueCreateModalLoad,
	globalIssueCreateModalLoadSubsequent,
} from '@jira/issue-create__issue-create-analytics/src/services/performance-analytics/index.tsx';
import {
	GENERIC_GLOBAL_CREATE_BUTTON,
	GENERIC_KEYBOARD_SHORTCUT,
	ISSUE_VIEW_CREATE_LINKED_ISSUE,
	JSW_ISSUE_VIEW_CREATE_ISSUE_IN_EPIC,
	JSW_ISSUE_VIEW_CREATE_SUBTASK,
	CREATE_ISSUE_ACTIVE_SPRINT_EMPTY_VIEW,
	JIRA_ISSUE_TABLE_TRIGGER,
	JWM_INLINE_CREATE,
	BOARD_CREATE,
	JSW_ADAPTED_GLOBAL_ISSUE_CREATE,
	JSW_ROADMAP_CREATE_ISSUE,
	JSW_CREATE_EPIC_ISSUE,
	CREATE_SUBTASK_NIN_TRIGGER,
	UC_ATLASSIAN_HOME_CREATE_ISSUE,
	ISSUE_CREATE_EMBEDDABLE_TRIGGER,
	ISSUE_CREATE_EMBEDDABLE_TRIGGER_PREFILLED,
} from './constants.tsx';
import type {
	MinimizableModalView,
	PersistentIssueCreateModalView,
	TriggerPointKeyType,
} from './types.tsx';

export const startGlobalIssueCreateMetrics = (
	isSubsequentLoad: boolean,
	isModernisedTrigger = false,
): void => {
	CreateIssueUFOExperience.start();
	const globalIssueCreateModalLoadMetric = isSubsequentLoad
		? globalIssueCreateModalLoadSubsequent
		: globalIssueCreateModalLoad;
	if (isSubsequentLoad) {
		if (!(isModernisedTrigger && fg('nike_united_fix_ufo_error'))) {
			traceUFOPress('global-issue.create-modal-subsequent');
		}
	} else if (!(isModernisedTrigger && fg('nike_united_fix_ufo_error'))) {
		traceUFOPress('global-issue.create-modal');
	}

	globalIssueCreateModalLoadMetric.start();
	startGlobalIssueCreateTTIWithAdjustments();
};

export const isPartOfDiscoverabilityTriggers = (triggerPoint?: TriggerPointKeyType): boolean => {
	if (!triggerPoint) {
		return false;
	}

	const acceptedTriggerPoints: TriggerPointKeyType[] = [
		'global-create-button',
		'keyboard-shortcut',
	];

	return acceptedTriggerPoints.includes(triggerPoint);
};

export const discoverabilityPushModalViewOverride = (
	shouldPushDiscoverability?: boolean,
	isTriggerAccepted?: boolean,
	defaultView: MinimizableModalView = 'modal',
):
	| {
			minimizableModalView: MinimizableModalView;
			shouldPushMiniModalDiscoverability?: boolean;
			shouldShowMiniModalSpotlight?: boolean;
	  }
	| undefined => {
	if (!shouldPushDiscoverability) {
		return undefined;
	}

	return {
		minimizableModalView: isTriggerAccepted ? 'mini' : defaultView,
		shouldPushMiniModalDiscoverability: true,
		shouldShowMiniModalSpotlight: isTriggerAccepted,
	};
};

export const getNextModalView = (
	isTriggerAccepted?: boolean,
	persistentView?: PersistentIssueCreateModalView,
	defaultView: MinimizableModalView = 'modal',
): MinimizableModalView => {
	if (isTriggerAccepted) {
		return persistentView || defaultView;
	}
	return defaultView;
};

/**
 * Trigger points that use the modernised issue create modal (and thus can switch to it
 * when the user selects a supported project from the legacy modal).
 */
export const TRIGGER_POINTS_USING_MODERNISED_MODAL: readonly TriggerPointKeyType[] = [
	GENERIC_KEYBOARD_SHORTCUT,
	GENERIC_GLOBAL_CREATE_BUTTON,
	ISSUE_VIEW_CREATE_LINKED_ISSUE,
	JSW_ISSUE_VIEW_CREATE_ISSUE_IN_EPIC,
	JSW_ISSUE_VIEW_CREATE_SUBTASK,
	CREATE_ISSUE_ACTIVE_SPRINT_EMPTY_VIEW,
	JIRA_ISSUE_TABLE_TRIGGER,
	JWM_INLINE_CREATE,
	BOARD_CREATE,
	JSW_ADAPTED_GLOBAL_ISSUE_CREATE,
	JSW_ROADMAP_CREATE_ISSUE,
	JSW_CREATE_EPIC_ISSUE,
	CREATE_SUBTASK_NIN_TRIGGER,
	UC_ATLASSIAN_HOME_CREATE_ISSUE,
];

/**
 * Trigger points that should open the modernised modal in expanded mode (no mini toggle).
 * Subset of TRIGGER_POINTS_USING_MODERNISED_MODAL (issue-view flows only).
 */
export const TRIGGER_POINTS_FORCE_EXPANDED_IN_MODERNISED: readonly TriggerPointKeyType[] = [
	ISSUE_VIEW_CREATE_LINKED_ISSUE,
	JSW_ISSUE_VIEW_CREATE_ISSUE_IN_EPIC,
	JSW_ISSUE_VIEW_CREATE_SUBTASK,
	ISSUE_CREATE_EMBEDDABLE_TRIGGER,
	ISSUE_CREATE_EMBEDDABLE_TRIGGER_PREFILLED,
];

/**
 * Returns true when the given trigger point should use the modernised issue create modal
 * (global create button or issue-view create linked / create in epic / create subtask).
 */
export const shouldTriggerModernisedIssueCreate = (
	triggerPointKey: TriggerPointKeyType,
): boolean => {
	return (
		expVal('create_work_item_modernization_exp', 'isEnabled', false) &&
		TRIGGER_POINTS_USING_MODERNISED_MODAL.includes(triggerPointKey)
	);
};

export const shouldTriggerModernisedIssueCreateEmbed = (
	triggerPointKey: TriggerPointKeyType,
): boolean => {
	return (
		expVal('create_work_item_modernization_exp', 'isEnabled', false) &&
		expVal('implement_modern_gic_for_embed_routes', 'isEnabled', false) &&
		(triggerPointKey === ISSUE_CREATE_EMBEDDABLE_TRIGGER ||
			triggerPointKey === ISSUE_CREATE_EMBEDDABLE_TRIGGER_PREFILLED)
	);
};

/**
 * Returns true when the modernised modal should open in expanded mode for this trigger point
 * (issue-view create linked, create in epic, create subtask, and the embed routes).
 */
export const shouldForceExpandedIssueCreateModal = (
	triggerPointKey: TriggerPointKeyType,
): boolean => {
	return (
		expVal('create_work_item_modernization_exp', 'isEnabled', false) &&
		TRIGGER_POINTS_FORCE_EXPANDED_IN_MODERNISED.includes(triggerPointKey)
	);
};
